import { useConvexAuth } from "convex/react";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { type ReactNode, useEffect } from "react";

import { env } from "~/env";
import {
  useActiveOrganization,
  useCurrentUser,
} from "~/integrations/vortex-auth/hooks";

import {
  identifyUser,
  initPostHog,
  resetUser,
  setLocationContext,
} from "./posthog";

/**
 * PostHog Provider with automatic auth user identification
 */
export function PostHogProvider({ children }: { children: ReactNode }) {
  // Initialize PostHog on mount
  useEffect(() => {
    initPostHog();
  }, []);

  if (!env.PUBLIC_POSTHOG_KEY) {
    return <>{children}</>;
  }

  return (
    <PHProvider
      apiKey={env.PUBLIC_POSTHOG_KEY}
      options={{
        api_host: env.PUBLIC_POSTHOG_HOST,
        // Most config is in initPostHog, but provider needs basics
        capture_pageview: false, // We handle this in initPostHog
        capture_pageleave: false,
      }}
    >
      {children}
    </PHProvider>
  );
}

/**
 * Component that syncs the vortex-auth user to PostHog.
 * Mounted inside the vortex-auth runtime + PostHogProvider in `main.tsx`.
 *
 * Note: org role is intentionally omitted — plasma's permission model lives on
 * employee records (`PERMISSION_KEYS`), not on the auth identity, so there is no
 * single "org role" claim to forward here. `createdAt` is likewise unavailable
 * from the vortex-auth user shape.
 */
export function PostHogUserSync() {
  const { isAuthenticated } = useConvexAuth();
  const { user } = useCurrentUser();
  const { organization } = useActiveOrganization();
  const posthog = usePostHog();

  const userId = user?.id;
  const email = user?.primaryEmailAddress?.emailAddress;
  const name = user?.fullName ?? undefined;
  const organizationId = organization?.id;
  const organizationName = organization?.name;

  useEffect(() => {
    if (!posthog) {
      return;
    }

    if (isAuthenticated && userId) {
      identifyUser({
        id: userId,
        email,
        name,
        organizationId,
        organizationName,
      });
    } else {
      resetUser();
    }
  }, [
    isAuthenticated,
    userId,
    email,
    name,
    organizationId,
    organizationName,
    posthog,
  ]);

  return null;
}

/**
 * Hook to set location context - call when location is selected/loaded
 */
export function usePostHogLocation() {
  const posthog = usePostHog();

  const setLocation = (location: {
    id: string;
    name: string;
    slug?: string;
    plan?: string;
    createdAt?: string;
  }) => {
    if (posthog) {
      setLocationContext(location);
    }
  };

  return { setLocation };
}
